import { httpStatusOf, PanelError, toPanelError } from '@/console/api/errors'
import { consoleRequest } from '@/console/api/http'
import {
  ChangeWarehouseStatusRequest,
  ChangeWarehouseStatusResponse,
  UpsertWarehousePolicyRequest,
  WarehouseAdminList,
  WarehouseListResponse,
  WarehouseView,
} from '@/console/api/types'

/**
 * LOGISPRD-15602 — 창고 정책 API 3개 (moms-be `WarehouseController`).
 * 셋 다 POST 이고 응답은 `{ meta, data }` 라 여기서 `.data` 를 벗겨 화면이 envelope 를 모르게 한다.
 */
type Envelope<T> = { data: T }

/**
 * 목록 · upsert · 활성 전환은 `@masterGate`(MASTER 또는 MOMS_ENG, FF 없음)다. 거절은 403 으로 오고(이전 BE 는 401),
 * `toPanelError` 는 둘 다 일반 forbidden 으로 접는다 — 이 문구로 원인을 좁혀 보여준다(`toPanelError(err, 이 상수)`).
 */
export const MASTER_GATE_FORBIDDEN_MESSAGE = '이 조치는 MASTER 또는 MOMS_ENG 권한이 있어야 해요.'

const HTTP_FORBIDDEN = 403

/**
 * masterGate 거절(403, 이전 BE 는 401)을 같은 권한 문구로 접는다. 400 은 `toPanelError` 가 BE `errorCode · message` 를
 * 그대로 보인다(문구를 복제하지 않는다). 목록 조회(data) · 정책 저장 · 활성 전환(tabs)이 모두 이 변환을 쓴다.
 */
export const toMasterGateError = (err: unknown): PanelError =>
  httpStatusOf(err) === HTTP_FORBIDDEN ? { kind: 'forbidden', message: MASTER_GATE_FORBIDDEN_MESSAGE } : toPanelError(err, MASTER_GATE_FORBIDDEN_MESSAGE)

// 활성 창고만 온다.
export const getAllWarehouses = async (): Promise<WarehouseView[]> => {
  const res = await consoleRequest.post<Envelope<WarehouseListResponse>>('/api/v1/get-all-warehouses', {})
  return res.data.items
}

// enum 에 없는 창고코드(BE `Warehouse.fromCode` UNKNOWN)는 data 가 null 이거나 키가 빠져 온다.
// React Query 는 undefined data 를 거부하므로 null 로 정규화한다.
export const getWarehouse = async (warehouseCode: string): Promise<WarehouseView | null> => {
  const res = await consoleRequest.post<Envelope<WarehouseView | null>, { warehouseCode: string }>('/api/v1/get-warehouse', { warehouseCode })
  return res.data ?? null
}

// 비활성 창고까지 포함한 관리자 목록. BE 원래 경로 `get-warehouse-admin-list` 는 ALB 가 경로에 `admin` 이 든 요청을
// 403 으로 막아(CORS 헤더 없이) 쓸 수 없어 `get-warehouse-master-list` 로 바꾼다. BE 가 바꾸기 전에는 404 → 활성 목록 폴백.
export const getWarehouseAdminList = async (): Promise<WarehouseAdminList> => {
  const res = await consoleRequest.post<Envelope<WarehouseAdminList>>('/api/v1/get-warehouse-master-list', {})
  return res.data
}

export const changeWarehouseStatus = async (request: ChangeWarehouseStatusRequest): Promise<ChangeWarehouseStatusResponse> => {
  const res = await consoleRequest.post<Envelope<ChangeWarehouseStatusResponse>, ChangeWarehouseStatusRequest>('/api/v1/change-warehouse-status', request)
  return res.data
}

// 응답 본문은 의미가 없다 — 저장 확인은 호출부가 재조회로 한다.
export const upsertWarehousePolicy = async (request: UpsertWarehousePolicyRequest): Promise<void> => {
  await consoleRequest.post<unknown, UpsertWarehousePolicyRequest>('/api/v1/upsert-warehouse-policy', request)
}
